import React, { useEffect, useState } from 'react';
import { object, func } from 'prop-types';
import { connect } from 'react-redux';

// utils
import ls from 'lib/core/storageFactory';
import { authActions } from 'redux-utils/actions/index';
import { appSelector } from 'redux-utils/selectors/index';

// assets
import backIcon from 'assets/svg/login_back_icon.svg';
import infivizLogo from 'assets/svg/infiviz_logo.svg';

// components
import StaticsContent from './StaticsContent';
import LoginVectorBackground from './LoginVectorBackground';
import Notification from 'components/Notification/Notification';
import LoginContainer from './LoginContainer';
import Cookies from 'universal-cookie';

// stylesheet
import './index.css';
import { LoginHeader } from './constants';
import { useLocation } from 'react-router-dom';
import store from 'redux-utils/store';
import { authTypes } from 'redux-utils/types/index';
import { deleteDB } from 'db';
import { track } from 'analytics';
import { LOGIN_PAGE_EVENTS } from 'analytics/events';

const cookies = new Cookies();

const Index = ({
  dispatchClientRequest,
  dispatchLoginRequest,
  dispatchForgotPasswordRequest,
  authState,
  location,
  history
}) => {
  const [isClientFieldActive, setClientFieldActive] = useState(true);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [hasSSOLogin, setHasSSOLogin] = useState(false);
  const [ssoDetails, setSsoDetails] = useState({
    showHybrid: false,
    text: '',
    link: '',
    lable: '',
    sso_logo: ''
  });
  const [notification, setNotification] = useState({
    display: false,
    message: ''
  });

  useEffect(() => {
    if (location.pathname.includes('forgotPassword')) {
      setForgotPassword(true);
    }
  }, [location.pathname]);

  // On mount: if newSElogout cookie is set, flush EVERYTHING and remove the flag
  useEffect(() => {
    const shouldHardLogout = cookies.get('newSElogout') === 'true';
    if (!shouldHardLogout) return;

    try {
      // Clear secure storage and known cookies via storageFactory
      ls.clear();
    } catch (e) {}

    try {
      window.localStorage && window.localStorage.clear();
    } catch (e) {}
    try {
      window.sessionStorage && window.sessionStorage.clear();
    } catch (e) {}
    try {
      deleteDB();
    } catch (e) {}
    try {
      if (window.caches && window.caches.keys) {
        window.caches
          .keys()
          .then(keys => keys.forEach(k => window.caches.delete(k)));
      }
    } catch (e) {}

    // Remove the flag itself from cookies across possible domains
    try {
      const domains = [
        undefined,
        `.${process.env.REACT_APP_SHELF_REDIRECT_URI}`,
        window.location.hostname
      ];
      domains.forEach(domain => {
        try {
          cookies.remove('newSElogout', { path: '/', domain });
        } catch (e) {}
      });
      try {
        document.cookie = 'newSElogout=; Max-Age=0; path=/';
        if (process.env.REACT_APP_SHELF_REDIRECT_URI) {
          document.cookie = `newSElogout=; Max-Age=0; path=/; domain=.${process.env.REACT_APP_SHELF_REDIRECT_URI}`;
        }
      } catch (e) {}
      ls.remove('newSElogout');
    } catch (e) {}

    // Reset entire redux state
    try {
      store.dispatch({ type: authTypes.forceClearAll });
    } catch (e) {}
  }, []);

  useEffect(() => {
    let timeout;
    if (authState.forgotPassword && authState.forgotPassword.message) {
      setNotification({
        display: true,
        message: authState.forgotPassword.message
      });
      timeout = setTimeout(() => {
        window.location = '/login';
      }, [3000]);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [authState.forgotPassword]);

  useEffect(() => {
    setClientFieldActive(!authState.client.client_exist);

    // check for these conditions -
    //     {
    //       SSO true, hybrid true - show SSO button
    //       SSO true, hybrid false - redirect user
    //       SSO false - username and pass
    //     }

    if (authState.client.sso && !authState.client.show_hybrid_sso) {
      if (process.env.REACT_APP_SHELF_DEPLOYMENT_ENV !== 'local') {
        setHasSSOLogin(authState.client.sso);
        window.location = authState.client.sso_link;
      } else {
        setHasSSOLogin(false);
      }
    } else if (authState.client.sso && authState.client.show_hybrid_sso) {
      setHasSSOLogin(authState.client.sso);

      setSsoDetails({
        showHybrid: authState.client.show_hybrid_sso,
        text: authState.client.text_on_sso_button,
        link: authState.client.sso_link,
        lable: authState.client.sso_description,
        sso_logo: authState.client.sso_logo
      });
    } else {
      setHasSSOLogin(false);
      setSsoDetails({
        showHybrid: false,
        text: '',
        link: '',
        lable: '',
        sso_logo: ''
      });
    }
  }, [authState.client]);

  const historyConst = useLocation();

  // Entra SSO failures 302 back here as /login?sso_error=<reason>. Surface a
  // friendly message via the existing Notification, then scrub the param.
  useEffect(() => {
    const ssoError = new URLSearchParams(location.search).get('sso_error');
    // [SSO-DEBUG] temporary rollout logging — grep tag to remove.
    // eslint-disable-next-line no-console
    console.log('[SSO-DEBUG][login] mounted on', window.location.href, '| sso_error =', ssoError);
    if (!ssoError) return;
    // Provider-neutral: both Google and Entra failures land here with the same
    // slugs, so messages must not name a specific provider.
    const messages = {
      access_denied:
        'Sign-in was cancelled. Please try again.',
      invalid_request:
        'Something went wrong starting sign-in. Please try again.',
      invalid_token:
        'Could not verify your sign-in. Please try again.',
      not_provisioned:
        'Your account is not set up for access. Please contact support.',
      server_error:
        'Sign-in failed due to a server error. Please try again.'
    };
    setNotification({
      display: true,
      message: messages[ssoError] || 'Sign-in failed. Please try again.'
    });
    window.history.replaceState({}, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authState.apiStatus.client.error) {
      setNotification({
        display: true,
        message: authState.apiStatus.client.message
      });
    }
    if (authState.apiStatus.login.error) {
      setNotification({
        display: true,
        message: authState.apiStatus.login.message
      });
    }
    if (authState.apiStatus.login.error) {
      setNotification({
        display: true,
        message: authState.apiStatus.login.message
      });
    }
    if (authState.apiStatus.forgotPassword.error) {
      setNotification({
        display: true,
        message: authState.apiStatus.forgotPassword.message
      });
    }
  }, [authState.apiStatus]);

  useEffect(() => {
    // If the hard-logout flag is present, skip any redirect logic this tick
    if (cookies.get('newSElogout') === 'true') return;
    if (authState.isLoggedIn || ls.get('isLoggedIn')) {
      ls.set('isLoggedIn', true);
      let pathname = '/app';

      if (cookies.get('prevPath')) {
        pathname = cookies.get('prevPath');

        if (cookies.get('searchPath')) {
          pathname = pathname + cookies.get('searchPath');
        }

        cookies.remove('prevPath', {
          path: '/',
          domain: `.${process.env.REACT_APP_SHELF_REDIRECT_URI}`,
          sameSite: 'none',
          secure: 'true'
        });
        cookies.remove('searchPath', {
          path: '/',
          domain: `.${process.env.REACT_APP_SHELF_REDIRECT_URI}`,
          sameSite: 'none',
          secure: 'true'
        });
      }
      if (historyConst.state?.from.pathname) {
        pathname = historyConst.state?.from.pathname;

        if (historyConst.state?.from.search)
          pathname = pathname + historyConst.state?.from.search;
      }
      if (authState.analyticsRedirect) {
        if (
          authState.analyticsRedirect.has_access &&
          authState.analyticsRedirect.dashboardType === 'tableau'
        ) {
          window.location.replace(authState.analyticsRedirect.redirect_url);
        } else if (process.env.REACT_APP_SHELF_DEPLOYMENT_ENV !== 'local') {
          const domain = ls.get('domain');
          window.location.replace(
            `https://${domain.toLowerCase()}.${
              process.env.REACT_APP_SHELF_REDIRECT_URI
            }${pathname}`
          );
        } else {
          window.location.replace(pathname);
        }
      } else if (process.env.REACT_APP_SHELF_DEPLOYMENT_ENV !== 'local') {
        const domain = ls.get('domain');
        window.location.replace(
          `https://${domain.toLowerCase()}.${
            process.env.REACT_APP_SHELF_REDIRECT_URI
          }${pathname}`
        );
      } else {
        window.location.replace('/app');
      }
    }
  }, [authState.isLoggedIn, authState.analyticsRedirect]);

  const handleNotificationClose = (_event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({
      display: false,
      message: ''
    });
  };

  const handleForgotPassword = () => {
    track(LOGIN_PAGE_EVENTS.FORGOT_PASSWORD_CLICKED, {});
    history.push('/login/forgotPassword');
  };
  const goPrevStep = () => {
    setHasSSOLogin(false);
    setSsoDetails({
      showHybrid: false,
      text: '',
      link: '',
      lable: ''
    });
    ls.clear();
    setClientFieldActive(true);
  };

  return (
    <div className="loginContainer">
      <Notification
        duration={3000}
        message={notification.message}
        isOpen={notification.display}
        handleClose={handleNotificationClose}
      />
      <div className="staticContentWrapper">
        <StaticsContent />
      </div>
      <div className="loginWrapper">
        <LoginVectorBackground />
        <div className="mobileViewHeader">{LoginHeader}</div>
        <div className="loginContainerWrapper">
          <div className="loginContainerHeader">
            <img src={infivizLogo} alt="infiviz_logo"></img>
          </div>
          <div className="loginContainerBody">
            <div role="button" className="loginBackButton">
              {!isClientFieldActive && (
                <img src={backIcon} alt="backIcon" onClick={goPrevStep} />
              )}
            </div>
            <div className="loginContainerBodyCentered">
              {/* Only show thi lable while user is being redirected */}
              {authState.client.sso &&
                !authState.client.show_hybrid_sso &&
                !isClientFieldActive &&
                hasSSOLogin && (
                  <p className="loginContainerLable">
                    <b>Please wait, you are being redirected. </b>
                  </p>
                )}

              <LoginContainer
                authState={authState}
                forgotPassword={forgotPassword}
                isClientFieldActive={isClientFieldActive}
                ssoDetails={ssoDetails}
                submitForgotPasswordEmail={dispatchForgotPasswordRequest}
                handleForgotPassword={handleForgotPassword}
                handleClientValidation={dispatchClientRequest}
                handleLoginValidation={dispatchLoginRequest}
                hasSSOLogin={hasSSOLogin}
                validationError={message => {
                  setNotification({
                    display: true,
                    message: message
                  });
                }}
              ></LoginContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// component properties
Index.propTypes = {
  location: object.isRequired,
  history: object.isRequired,
  authState: object.isRequired,
  dispatchClientRequest: func.isRequired,
  dispatchLoginRequest: func.isRequired,
  dispatchForgotPasswordRequest: func.isRequired
};

/*
    Connect redux store state to props so that you can access the state
    from the scope of the component's props
  */
const mapStateToProps = state => ({
  authState: appSelector.getAuthState(state)
});

/*
    Connect dispatch methods to props so that you can call the methods
    from the scope of the component's props
  */
const mapDispatchToProps = dispatch => ({
  dispatchLoginRequest: payload =>
    dispatch(authActions.userLoginRequest(payload)),
  dispatchClientRequest: payload =>
    dispatch(authActions.clientValidationRequest(payload)),
  dispatchForgotPasswordRequest: payload =>
    dispatch(authActions.forgotPasswordRequest(payload))
});

export default connect(mapStateToProps, mapDispatchToProps)(Index);
